import { useState, useEffect } from "react";
import { LayoutGrid, ArrowLeft } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const CandidateLogin = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { candidateLogin, verifyOtp } = useAuth();

  // Load email from URL parameters on component mount
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [searchParams]);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      alert("Please enter your email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await candidateLogin(email);
      if (success) {
        setOtpSent(true);
      }
    } catch (error) {
      console.error("Error requesting OTP:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      alert("Please enter a valid 6-digit verification code");
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await verifyOtp(email, otp);
      if (success) {
        // Navigate to candidate dashboard or exam screen
        navigate("/candidate");
      } else {
        // Clear OTP input on failure but stay on OTP screen
        setOtp("");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      // Clear OTP input on error but stay on OTP screen
      setOtp("");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header Section */}
      <div className="flex items-center justify-center pt-4 pb-4 flex-shrink-0">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="flex items-center">
            <LayoutGrid className="h-8 w-8 text-primary" />
            <h1 className="ml-2 text-2xl font-bold">AssessPro</h1>
          </div>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900">
            Candidate Access
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {!otpSent
              ? "Enter your email to receive a verification code"
              : `Enter the verification code sent to ${email}`}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 min-h-0">
        <div className="w-full max-w-6xl flex items-center justify-center h-full">
          <div className="w-full h-full max-h-full flex flex-row overflow-hidden shadow-2xl bg-white rounded-lg">
            {/* Left side - Image Section */}
            <div className="w-full lg:w-1/2 min-h-64 lg:min-h-full hidden lg:block bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-6">
              <div className="text-center w-full h-full flex items-center justify-center">
                <img
                  src="https://cdn4.iconfinder.com/data/icons/interface-118/200/enter-password-3--enter-password-INTERFACE-USER-UI-CREDENTIALS-LOGIN-SESSION-ACCOUNT-LOCK-KEY-WOMAN-512.png"
                  alt="Candidate Assessment"
                  className="w-full h-full max-h-96 lg:max-h-full object-cover rounded-xl shadow-lg"
                />
              </div>
            </div>

            {/* Right side - Form Section */}
            <div className="w-full lg:w-1/2 flex flex-col">
              <div className="flex-1 flex flex-col justify-center py-8 px-8">
                {!otpSent ? (
                  <div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium">
                          Email
                        </label>
                        <input
                          id="email"
                          type="email"
                          placeholder="your.email@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <button
                        onClick={handleRequestOtp}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md disabled:opacity-50"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Sending..." : "Get Verification Code"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label htmlFor="otp" className="text-sm font-medium">
                          Verification Code
                        </label>
                        <input
                          id="otp"
                          type="text"
                          placeholder="6-digit code"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          maxLength={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <button
                        onClick={handleVerifyOtp}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md disabled:opacity-50"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Verifying..." : "Verify & Login"}
                      </button>
                      <button
                        onClick={() => setOtpSent(false)}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-md flex items-center justify-center"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateLogin;